import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";

import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import GroupSchema from "../../schemas/materialGroup.schema";
import MaterialGroupsAPI from "../../API/materialGroups.api";
import { trimValues } from "../../utils/Helpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import ValidationError from "../Shared/ValidationError";
import { validate } from "../../utils/ValidationHelpers";
import BaseModal from "./BaseModal";
import EditableImage from "../../components/Images/EditableImages";
import { ENTITIES, ACTIONS } from "../../utils/LogManagement/LogConstants";

const MaterialGroupModal = ({
    modalButton,
    modalButtonSetter,
    completionHandler,
    data,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [error, setError] = useState({});
    const [group, setGroup] = useState({});

    const { t } = useTranslation();

    useEffect(() => {
        if (data) {
            setGroup({
                name: data?.group,
                description: data?.description,
                image: data?.image,
            });
        } else {
            setGroup({});
        }
    }, [data]);

    useEffect(() => {
        if (modalButton) {
            openModal();
            modalButtonSetter(false);
        }
    }, [modalButton]);

    useEffect(() => {
        validate(
            group,
            GroupSchema.CreateAndEditMaterialGroupSchema,
            setIsFormValid,
            setError
        );
    }, [group]);

    const openModal = () => {
        setIsVisible(true);
    };

    const closeModal = () => {
        setGroup({});
        setIsVisible(false);
        setIsSubmitting(false);
        setIsFormSubmitted(false);
        setIsFormValid(false);
    };

    const handleSubmit = () => {
        setIsSubmitting(true);
        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        const newGroup = trimValues(group);
        if (data) {
            MaterialGroupsAPI.updateMaterialGroup({
                id: data?.id,
                ...newGroup,
            })
                .then((res) => {
                    if (res?.modified) {
                        SuccessAlert(t("editGroupSuccessMessage"));
                        LogCapture.info(
                            `Material group updated with name: ${newGroup?.name} & id: ${data?.id}`,
                            ENTITIES.materialGroups,
                            ACTIONS.materialGroups.update
                        );
                        completionHandler();
                    } else {
                        ErrorAlert(t("editGroupFailMessage"));
                        LogCapture.error(
                            `Couldn't update material group with name: ${newGroup?.name} & id: ${data?.id}`
                        );
                    }
                })
                .catch(() => {
                    ErrorAlert(t("editGroupFailMessage"));
                    LogCapture.error(
                        `Couldn't update material group with name: ${newGroup?.name} & id: ${data?.id}, error: ${error}`
                    );
                    setIsSubmitting(false);
                })
                .finally(() => {
                    closeModal();
                });
        } else {
            MaterialGroupsAPI.addMaterialGroup({
                ...newGroup,
            })
                .then((res) => {
                    // Created new group
                    if (res?.inserted) {
                        SuccessAlert(t("createGroupSuccessMessage"));
                        LogCapture.info(
                            `Material group created with name: ${newGroup?.name}`,
                            ENTITIES.materialGroups,
                            ACTIONS.materialGroups.create
                        );
                        completionHandler();
                    } else {
                        LogCapture.error(
                            `Couldn't create material group with name: ${newGroup?.name}`
                        );
                        ErrorAlert(t("createGroupFailMessage"));
                    }
                })
                .catch(() => {
                    ErrorAlert(t("createGroupFailMessage"));
                    LogCapture.error(
                        `Couldn't create material group with name: ${newGroup?.name}, error: ${error}`
                    );
                    setIsSubmitting(false);
                })
                .finally(() => {
                    closeModal();
                });
        }
    };

    const handleInputChange = (e) => {
        setGroup({
            ...group,
            [e.target.name]: e.target.value,
        });
    };

    const fields = [
        {
            name: "name",
            label: (
                <label className="uppercase required">
                    <Trans i18nKey="name">Name</Trans>
                </label>
            ),
            input: (
                <input
                    onChange={handleInputChange}
                    value={group?.name || ""}
                    name="name"
                    type="text"
                    placeholder="Ex. Corn Silage"
                    className="input-element"
                />
            ),
        },
        {
            name: "description",
            label: (
                <label className="uppercase">
                    <Trans i18nKey="description">Description</Trans>
                </label>
            ),
            input: (
                <textarea
                    name="description"
                    value={group?.description || ""}
                    onChange={handleInputChange}
                    placeholder="Type your description here..."
                    className="input-element textarea-element"
                ></textarea>
            ),
        },
    ];

    const renderUploadImage = () => {
        return (
            <EditableImage
                state={group}
                stateSetter={setGroup}
                defaultImage={
                    require("../../assets/images/group-material.png").default
                }
            />
        );
    };

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                return (
                    <div
                        key={index}
                        className={`input-group ${
                            error[field.name] && isFormSubmitted
                                ? "invalid-warning-input"
                                : ""
                        }`}
                    >
                        {field.label}
                        {field.input}
                        {isFormSubmitted && (
                            <ValidationError errorMessage={error[field.name]} />
                        )}
                    </div>
                );
            })
        );
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isSubmitting}
                title={data ? t("buttonEditGroup") : t("buttonCreateNewGroup")}
                action={{
                    submitAction: handleSubmit,
                    name: data ? t("edit") : t("add"),
                }}
                isSmall
            >
                <div className="modal-content">
                    <div className="main-customer-form-inputs">
                        <div className="upload-img-box flex">
                            <div className="relative">
                                {renderUploadImage()}
                            </div>
                            <div className="w-full">{renderFields()}</div>
                        </div>
                    </div>
                </div>
            </BaseModal>
        </>
    );
};

export default MaterialGroupModal;
